import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { Input } from "@/shared/ui/input";
import {
  Truck, ArrowLeft, ChevronRight, ChevronDown, FolderOpen, Download,
  CheckCircle2, Plus, Trash2, Pencil, Send, MessageSquare, Clock, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  submitted: { label: "Awaiting procurement", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800" },
  procured: { label: "Procured", cls: "bg-emerald-100 text-emerald-700" },
};

async function downloadJourney() {
  try {
    const res = await api.get("/procurement-journey.xlsx", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "Procurement-Journey.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
}

export default function PSMProcurementPortal() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState(null);
  const [opts, setOpts] = useState({ materials: [], thickness: [], colours: [] });

  const load = useCallback(() => {
    api.get("/procurement-board")
      .then((r) => { setData(r.data.projects || []); setOpts(r.data.options || {}); })
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); setData([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (data || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto" data-testid="psm-procurement-page">
      {data === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} opts={opts} onBack={() => setCode(null)} onChanged={load} />
      ) : (
        <ProjectList projects={data} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="psm-procurement-list">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
            <Truck size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Procurement</h1>
            <p className="text-sm text-zinc-500">Materials requested by designers — track, edit & push to procured.</p>
          </div>
        </div>
        <button onClick={downloadJourney} data-testid="procurement-journey-download"
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3.5 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          <Download size={15} /> Procurement Journey (Excel)
        </button>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl"
          data-testid="psm-procurement-empty">
          No material requests yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)}
              data-testid={`psm-procurement-project-${p.project_code}`}
              className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-indigo-300 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                <ChevronRight size={16} className="text-zinc-300" />
              </div>
              <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.project_name}</div>
              {p.client_name && <div className="text-xs text-zinc-400">{p.client_name}</div>}
              <div className="mt-4 text-sm">
                {p.pending_count > 0 ? (
                  <span className="text-amber-700 font-medium">{p.pending_count} to procure</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircle2 size={14} /> all procured
                  </span>
                )}
                <span className="text-zinc-400"> · {p.total} request(s)</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectView({ project, opts, onBack, onChanged }) {
  return (
    <div data-testid="psm-procurement-view">
      <button onClick={onBack} data-testid="psm-procurement-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="mb-6">
        <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={project.project_code} /></span>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.project_name}</h1>
        {project.client_name && <p className="text-sm text-zinc-500">{project.client_name}</p>}
      </div>
      <div className="space-y-4">
        {project.items.map((it) => (
          <ProcCard key={it.boq_id} it={it} code={project.project_code} opts={opts} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

const BLANK_ITEM = { kind: "standard", material: "", thickness: "", colour: "", spec: "", source: "", quantity: "", unit: "Nos", remarks: "" };

function ProcCard({ it, code, opts, onChanged }) {
  const meta = STATUS_META[it.status] || STATUS_META.submitted;
  const actionable = it.status !== "procured";
  const [open, setOpen] = useState(actionable);
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState(it.items.map((x) => ({ ...x })));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function saveItems() {
    const clean = items.filter((i) => (i.kind === "standard" ? i.material : i.spec) && Number(i.quantity) > 0);
    if (clean.length === 0) { toast.error("Add at least one item with material + quantity"); return; }
    setBusy(true);
    try {
      await api.put(`/procurement/${code}/${it.boq_id}/items`, { items: clean.map((i) => ({ ...i, quantity: Number(i.quantity) })) });
      toast.success("Material list updated"); setEditing(false); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  }
  async function setStatus(status) {
    setBusy(true);
    try {
      await api.patch(`/procurement/${code}/${it.boq_id}/status`, { status });
      toast.success(status === "procured" ? "Marked procured" : "Marked in progress"); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  }
  async function addNote() {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/procurement/${code}/${it.boq_id}/note`, { text: note.trim() });
      setNote(""); onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setBusy(false); }
  }

  return (
    <div className={`rounded-2xl border bg-white shadow-sm overflow-hidden border-l-4 ${actionable ? "border-l-amber-400" : "border-l-emerald-400"} border-zinc-200`}
      data-testid={`psm-proc-card-${it.boq_id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`psm-proc-toggle-${it.boq_id}`}
        className="w-full px-5 py-3.5 flex items-center gap-3 text-left">
        <ChevronDown size={16} className={`text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 truncate">{[it.wall_no, it.line_name].filter(Boolean).join(" · ")}</div>
          <div className="text-xs text-zinc-400 truncate">
            {it.group ? `${it.group} · ` : ""}designer {it.designer_name || "—"} · {it.items.length} item(s)
          </div>
        </div>
        <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${meta.cls}`}
          data-testid={`psm-proc-status-${it.boq_id}`}>{meta.label}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {/* Items */}
          {!editing ? (
            <div className="rounded-xl border border-zinc-200 overflow-hidden">
              {it.items.length === 0 ? (
                <div className="px-3 py-4 text-sm text-zinc-400">No items declared yet.</div>
              ) : it.items.map((m, idx) => (
                <div key={idx} className="px-3 py-2.5 border-b border-zinc-100 last:border-0 flex items-start justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-medium text-zinc-800">{m.kind === "non_standard" ? (m.spec || "Non-standard") : (m.material || "—")}</span>
                    <span className="text-zinc-400">
                      {(m.thickness_custom || m.thickness) ? ` · ${m.thickness_custom || m.thickness}` : ""}
                      {(m.colour_custom || m.colour) ? ` · ${m.colour_custom || m.colour}` : ""}
                    </span>
                    {m.source && <div className="text-xs text-zinc-400">Source: {m.source}</div>}
                    {m.remarks && <div className="text-xs text-zinc-400">{m.remarks}</div>}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono shrink-0">{m.quantity} {m.unit}
                    <span className={`ml-2 px-1.5 py-0.5 rounded ${m.kind === "non_standard" ? "bg-rose-50 text-rose-600" : "bg-zinc-100 text-zinc-500"}`}>{m.kind === "non_standard" ? "NS" : "STD"}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ItemEditor items={items} setItems={setItems} opts={opts} boqId={it.boq_id} />
          )}

          {/* Item actions */}
          <div className="flex flex-wrap items-center gap-2">
            {!editing ? (
              <button onClick={() => { setItems(it.items.map((x) => ({ ...x }))); setEditing(true); }}
                data-testid={`psm-proc-edit-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
                <Pencil size={14} /> Add / edit materials
              </button>
            ) : (
              <>
                <button onClick={saveItems} disabled={busy} data-testid={`psm-proc-save-${it.boq_id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-3.5 py-1.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save materials
                </button>
                <button onClick={() => setEditing(false)} className="rounded-lg border border-zinc-300 px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-50">Cancel</button>
              </>
            )}
            {!editing && it.status === "submitted" && (
              <button onClick={() => setStatus("in_progress")} disabled={busy} data-testid={`psm-proc-inprogress-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 text-white px-3.5 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                <Clock size={14} /> Mark in progress
              </button>
            )}
            {!editing && it.status !== "procured" && (
              <button onClick={() => setStatus("procured")} disabled={busy} data-testid={`psm-proc-procured-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-3.5 py-1.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 size={14} /> Mark procured
              </button>
            )}
          </div>

          {/* Communication thread */}
          <div className="rounded-xl border border-zinc-200 p-3">
            <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2">
              <MessageSquare size={12} /> Communication
            </div>
            <div className="space-y-2 mb-3 max-h-44 overflow-y-auto">
              {(it.thread || []).length === 0 ? (
                <p className="text-xs text-zinc-400">No notes yet.</p>
              ) : it.thread.map((t) => (
                <div key={t.id} className="text-sm">
                  <span className="font-medium text-zinc-800">{t.by_name}</span>
                  <span className="text-xs text-zinc-400"> · {t.by_role} · {(t.at || "").slice(0, 16).replace("T", " ")}</span>
                  <p className="text-zinc-600">{t.text}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the team…"
                onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid={`psm-proc-note-input-${it.boq_id}`} />
              <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`psm-proc-note-send-${it.boq_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemEditor({ items, setItems, opts, boqId }) {
  const upd = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const add = () => setItems([...items, { ...BLANK_ITEM }]);

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-xl border border-zinc-200 p-3 space-y-2.5" data-testid={`psm-proc-item-${boqId}-${idx}`}>
          <div className="flex items-center justify-between">
            <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden text-xs">
              {["standard", "non_standard"].map((k) => (
                <button key={k} onClick={() => upd(idx, { kind: k })}
                  className={`px-3 py-1 ${it.kind === k ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}>
                  {k === "standard" ? "Standard" : "Non-standard"}
                </button>
              ))}
            </div>
            <button onClick={() => remove(idx)} className="text-zinc-400 hover:text-rose-600" data-testid={`psm-proc-item-remove-${boqId}-${idx}`}>
              <Trash2 size={15} />
            </button>
          </div>
          {it.kind === "standard" ? (
            <div className="grid grid-cols-2 gap-2">
              <select value={it.material || ""} onChange={(e) => upd(idx, { material: e.target.value })}
                className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm" data-testid={`psm-proc-item-material-${boqId}-${idx}`}>
                <option value="">Material…</option>
                {(opts.materials || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={it.thickness || ""} onChange={(e) => upd(idx, { thickness: e.target.value })}
                className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm">
                <option value="">Thickness…</option>
                {(opts.thickness || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={it.colour || ""} onChange={(e) => upd(idx, { colour: e.target.value })}
                className="rounded-lg border border-zinc-200 px-2 py-1.5 text-sm">
                <option value="">Colour…</option>
                {(opts.colours || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <Input placeholder="Remarks" value={it.remarks || ""} onChange={(e) => upd(idx, { remarks: e.target.value })} />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <Input placeholder="Material / specification" value={it.spec || ""} onChange={(e) => upd(idx, { spec: e.target.value })}
                data-testid={`psm-proc-item-spec-${boqId}-${idx}`} />
              <Input placeholder="Procurement source" value={it.source || ""} onChange={(e) => upd(idx, { source: e.target.value })} />
              <Input placeholder="Remarks" value={it.remarks || ""} onChange={(e) => upd(idx, { remarks: e.target.value })} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.01" placeholder="Quantity" value={it.quantity || ""} onChange={(e) => upd(idx, { quantity: e.target.value })}
              data-testid={`psm-proc-item-qty-${boqId}-${idx}`} />
            <Input placeholder="Unit" value={it.unit || ""} onChange={(e) => upd(idx, { unit: e.target.value })} />
          </div>
        </div>
      ))}
      <button onClick={add} data-testid={`psm-proc-additem-${boqId}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-300 px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-50">
        <Plus size={14} /> Add material line
      </button>
    </div>
  );
}
